import { useQuery } from '@apollo/client';
import { DescriptionPage_LearningProductType as ProductVariant } from '__generated__/graphql-types';

import type { Recommender } from '@coursera/event-pulse-types';

import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import XdpProductRecommendationsQuery from 'bundles/unified-description-page-common/queries/XdpProductRecommendations.graphql';
import type {
  XdpRec_ProductCardFragment as ProductCard,
  XdpRecommendedQuery,
  XdpRecommendedQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/XdpProductRecommendations';
import { convertProductCardS12nOrCourseToCollectionEntity } from 'bundles/unified-description-page-common/utils/dataTransformation';

type ProductVariantValue = (typeof ProductVariant)[keyof typeof ProductVariant];

type Props = {
  productVariant: ProductVariantValue;
  productId?: string;
  skip: boolean;
  showOnlyCplusEligibleRecs: boolean;
};

export type XdpRecommendationsQueryData = {
  eventingRecommender: Recommender | undefined;
  collectionTrackingId: string;
  recommendationCollectionEntities: CollectionEntity[];
  loading: boolean;
};

const idToPrefixedStringkey = (id: string, productVariant: ProductVariantValue) => {
  // escape each ~ in the id with !~, but each tilde has one more ! than the previous one
  // ex: s12n~child~asdf~ghjkl -> s12n~child!~asdf!!~ghjkl
  // this is needed for proper stringkey formatting:
  // https://github.com/webedx-spark/shared-java-application-libraries/blob/main/libraries/client/src/main/java/org/coursera/common/clients/stringkey/README.md#records
  const parts = id.split('~');
  const result = parts.map((part, i) => {
    if (i === 0) return part;
    const exclamations = '!'.repeat(i);
    return `${exclamations}~${part}`;
  });
  const escapedId = result.join('');

  let prefix = '';
  if (productVariant === ProductVariant.Course) prefix = 'course';
  else prefix = 's12n';

  return `${prefix}~${escapedId}`;
};

const isProductCardEntity = (product: unknown): product is ProductCard => {
  return (product as ProductCard).id !== undefined;
};

const resultsToCollectionEntities = (data: XdpRecommendedQuery | undefined): CollectionEntity[] => {
  if (!data) return [];
  const products = data.Collection?.queryCollection.entities?.elements ?? [];
  const entities: CollectionEntity[] = products
    ?.filter(isProductCardEntity)
    .map((product, index) => convertProductCardS12nOrCourseToCollectionEntity(product, index))
    .filter(Boolean);
  return entities;
};

const useXdpRecommendationsQuery = ({
  productVariant,
  productId,
  skip: skipOverride,
  showOnlyCplusEligibleRecs,
}: Props): XdpRecommendationsQueryData => {
  const { data, loading } = useQuery<XdpRecommendedQuery, XdpRecommendedQueryVariables>(
    XdpProductRecommendationsQuery,
    {
      context: { clientName: 'gatewayGql' },
      variables: {
        // we can safely add the fallback for productId here because the query does not actually run if programId is falsy
        programPrefixedStringKey: idToPrefixedStringkey(productId ?? '', productVariant),
        removeNonCourseraPlus: showOnlyCplusEligibleRecs,
      },
      skip: skipOverride || !productId,
    }
  );

  const recommender: Recommender | undefined = data?.Collection?.queryCollection?.recommenderConfigurationHash
    ? {
        context: data.Collection.queryCollection.context,
        hash: data.Collection.queryCollection.recommenderConfigurationHash,
      }
    : undefined;

  return {
    eventingRecommender: recommender,
    recommendationCollectionEntities: resultsToCollectionEntities(data),
    collectionTrackingId: data?.Collection?.queryCollection?.collectionTrackingId ?? '',
    loading,
  };
};

export default useXdpRecommendationsQuery;
